import { useState } from 'react';
import { Upload, Spin, Image, message } from 'antd-v5';
import { PlusOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import ReplaceIcon from './ReplaceIcon';
import { useLang } from '@/i18n';

/**
 * 无文件：显示大 "+" 触发区（虚线框）
 * 上传中：显示本地预览 + loading 遮罩
 * 已上传：hover 时显示预览/删除操作遮罩 + 下方 Replace 按钮
 */

interface PictureUploadProps {
    value?: string | null;
    onChange?: (url: string | null) => void;
    uploadFn: (file: File) => Promise<string>;
}

const PictureUpload: React.FC<PictureUploadProps> = ({ value, onChange, uploadFn }) => {
    const { t } = useLang('VerifySnModal');
    const [uploading, setUploading] = useState(false);
    const [localPreview, setLocalPreview] = useState<string | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const hasFile = !!value;
    const previewSrc = localPreview || value;

    const doUpload = async (file: File) => {
        setUploading(true);
        setLocalPreview(URL.createObjectURL(file));
        try {
            const url = await uploadFn(file);
            onChange?.(url);
        } catch {
            message.error('Image upload failed, please try again.');
            setLocalPreview(null);
            onChange?.(null);
        } finally {
            setUploading(false);
        }
    };

    const customRequest = (options: any) => {
        doUpload(options.file as File);
    };

    const handleDelete = () => {
        setLocalPreview(null);
        onChange?.(null);
    };

    if (hasFile || uploading) {
        return (
            <div className={styles.uploadPreviewBlock}>
                <div className={styles.uploadPreviewImg}>
                    {previewSrc && <img src={previewSrc} alt="preview" />}
                    {uploading ? (
                        <div className={styles.uploadOverlay}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.uploadHoverOverlay}>
                            <EyeOutlined onClick={() => setPreviewOpen(true)} />
                            <DeleteOutlined onClick={handleDelete} />
                        </div>
                    )}
                </div>

                {previewSrc && <Image src={previewSrc} style={{ display: 'none' }} preview={{ visible: previewOpen, onVisibleChange: setPreviewOpen }} />}

                <Upload customRequest={customRequest} showUploadList={false} accept="image/*" disabled={uploading}>
                    <div className={`${styles.uploadReplace} ${uploading ? styles.uploadReplaceDisabled : ''}`}>
                        {/* <SyncOutlined spin={uploading} /> */}
                        <ReplaceIcon />
                    </div>
                </Upload>
            </div>
        );
    }

    return (
        <div className={styles.uploadWrap}>
            <Upload customRequest={customRequest} showUploadList={false} accept="image/*">
                <div className={styles.uploadTrigger}>
                    <div className={styles.uploadPlus}>
                        <PlusOutlined />
                    </div>
                    <span className={styles.uploadText}>{t('click_to_upload')}</span>
                </div>
            </Upload>
        </div>
    );
};

export default PictureUpload;
